<template>
  <div
    class="overflow-hidden relative w-full h-full flex items-center justify-center"
  >
    <img
      v-if="displayMode === 'normal' || !canBeHappy"
      ref="refMe"
      :src="`${require(`@/assets/images/icon/${iconName}.webp`)}`"
      class="trans"
      alt=""
    />
    <img
      v-else-if="happinessLevel === 1"
      ref="refMe"
      :class="{
        'w-4 h-4': showBonusHappiness,
        'w-8 h-8': !showBonusHappiness,
      }"
      :src="`${require('@/assets/images/happiness1.png')}`"
      alt=""
      class="trans"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    />
    <img
      v-else-if="happinessLevel === 2"
      ref="refMe"
      :class="{
        'w-4 h-4': showBonusHappiness,
        'w-8 h-8': !showBonusHappiness,
      }"
      :src="`${require('@/assets/images/happiness2.png')}`"
      alt=""
      class="trans"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    />
    <img
      v-else-if="happinessLevel === 3"
      ref="refMe"
      :class="{
        'w-4 h-4': showBonusHappiness,
        'w-8 h-8': !showBonusHappiness,
      }"
      :src="`${require('@/assets/images/happiness3.png')}`"
      alt=""
      class="trans"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    />
    <img
      v-else
      ref="refMe"
      :class="{
        'w-4 h-4': showBonusHappiness,
        'w-8 h-8': !showBonusHappiness,
      }"
      :src="`${require('@/assets/images/happiness0.png')}`"
      alt=""
      class="trans"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    />
  </div>
</template>

<script>
import BuildingType, {isWorkShop} from "@/roc/building/BuildingType";
import Grid from "@/roc/grid/Grid";
import {computed, onMounted, ref, watch} from 'vue';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';
import {useLayoutStore} from '@/roc/stores/LayoutStore';
import Rect from '@/roc/grid/Rect';

export default {
  props: {
    happinessLevel: {
      type: Number,
      required: true
    },
    templateId: {
      type: String,
      required: true
    },
    hasPending: {
      type: Boolean,
      required: true
    },
    rotated: {
      type: Boolean,
      required: true
    }
  },
  setup (props) {
    const appStore = useApplicationStore();
    const lytStore = useLayoutStore();

    /** @type {Ref<HTMLImageElement>} */
    const refMe = ref(null);
    /** @type {BuildingTemplate} */
    const thisTemplate = lytStore.getBuildingTemplateById(props.templateId);

    const toggleCultureBonusMarkerDisplay = (building, isVisible) => {
      if (appStore.displayMode === 'normal' || props.happinessLevel === 0) {
        return;
      }
      if (props.hasPending) {
        return;
      }

      const happiness = thisTemplate.effects.getHappiness();
      if (!happiness) {
        return;
      }

      const cbBuildings = document.getElementsByClassName("culture-bonus");

      const myRect = new Rect(
        parseInt(building.style.left.replace("px", "")),
        parseInt(building.style.top.replace("px", "")),
        building.clientWidth - 1,
        building.clientHeight - 1
      );

      for (let i = cbBuildings.length - 1; i >= 0; i--) {
        /** @type {HTMLDivElement} */
        const cbBuilding = cbBuildings[i];

        const range = parseInt(
          cbBuilding.dataset.buildingCultureBonusRange
        );

        const bonusRect = new Rect(
          parseInt(cbBuilding.style.left.replace("px", "")) - range * Grid.CELL_SIZE,
          parseInt(cbBuilding.style.top.replace("px", "")) - range * Grid.CELL_SIZE,
          cbBuilding.clientWidth - 1 + 2 * range * Grid.CELL_SIZE,
          cbBuilding.clientHeight - 1 + 2 * range * Grid.CELL_SIZE
        );

        if (myRect.intersects(bonusRect)) {
          const marker = document.querySelector('[data-marked-building="' + cbBuilding.dataset.buildingReference + '"]');
          if (isVisible) {
            cbBuilding.classList.add("emphasize");
            marker.classList.remove("hidden");
          } else {
            cbBuilding.classList.remove("emphasize");
            marker.classList.add("hidden");
          }
        }
      }
    };

    const updateImageDimensions = () => {
      if (!refMe.value) {
        return;
      }

      const SCALE_FACTOR = 0.85;
      const maxW = refMe.value.parentNode.clientWidth;
      const maxH = refMe.value.parentNode.clientHeight;
      let dstW = refMe.value.width, dstH = refMe.value.height;

      while (dstW > maxW || dstH > maxH) {
        if (dstW > maxW) {
          dstW = maxW;
          dstH = Math.round((parseInt(refMe.value.dataset.originalHeight) * maxW) / parseInt(refMe.value.dataset.originalWidth));
        } else if (dstH > maxH) {
          dstH = maxH;
          dstW = Math.round((parseInt(refMe.value.dataset.originalWidth) * maxH) / parseInt(refMe.value.dataset.originalHeight));
        }
      }

      refMe.value.width = dstW * SCALE_FACTOR;
      refMe.value.height = dstH * SCALE_FACTOR;
    };

    watch(() => props.rotated, updateImageDimensions);

    onMounted(() => {
      refMe.value.onload = function () {
        if (!refMe.value) {
          return;
        }
        if (this.width === this.height) {
          return;
        }
        if (thisTemplate.effects.getHappiness() !== null || appStore.displayMode !== 'normal') {
          return;
        }

        refMe.value.dataset.originalWidth = this.width;
        refMe.value.dataset.originalHeight = this.height;

        updateImageDimensions();
      };
    });

    return {
      refMe,

      iconName: computed(() => {
        if (typeof thisTemplate.icon === 'function') {
          return thisTemplate.icon(appStore.selectedEra);
        }
        return thisTemplate.icon || 'Unknown';
      }),
      canBeHappy: computed(() => {
        return thisTemplate.effects.getHappiness() !== null;
      }),
      hasCustomIcon: computed(() => {
        return thisTemplate.type === BuildingType.EVENT ||
          BuildingType.COLLECTABLE === thisTemplate.type;
      }),
      displayMode: computed(() => {
        return appStore.displayMode;
      }),
      showBonusHappiness: computed(() => {
        return appStore.showBonusHappiness;
      }),

      onMouseEnter() {
        const building = refMe.value.parentNode.parentNode.parentNode;
        toggleCultureBonusMarkerDisplay(building, true);
      },
      onMouseLeave() {
        const building = refMe.value.parentNode.parentNode.parentNode;
        toggleCultureBonusMarkerDisplay(building, false);
      }
    };
  }
};
</script>
